import DOMPurifyFactory from 'dompurify';
import { marked } from 'marked';

import { renderBodyWithChipLinks } from './mentionChipUtils';
import { injectCitationMarkers } from './WebSearch/citationUtils';
import { CitationMarker } from './WebSearch/webSearch.types';

// Configure marked for safe HTML output (shared singleton config used by both
// the final render pipeline and the streaming pipeline).
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Tilde strikethrough is not used in agent output. Intercept del patterns and emit
// them as plain text tokens so adjacent formatting like **bold** is never disrupted.
marked.use({
  extensions: [
    {
      name: 'del',
      level: 'inline',
      start: (src: string) => src.indexOf('~'),
      tokenizer(src: string) {
        // Match only the opening tilde(s), not the full span. This prevents del
        // from consuming bold markers (**) that appear between two tildes.
        const match = /^(~~?)(?=[^\s~])/.exec(src);
        if (match) {
          return { type: 'text', raw: match[1], text: match[1] };
        }
        return undefined;
      },
    },
  ],
});

export const CONVERSATIONS_PER_PAGE = 25;

export const wrapTablesForScroll = (html: string): string =>
  html.replace(/<table/g, '<div class="ai-table-scroll"><table').replace(/<\/table>/g, '</table></div>');
export const CONVERSATION_MAX_TITLE_LENGTH = 100;

export type ConversationListItem = {
  id: string;
  title: string;
  lastUserMessageAt?: string | null;
};

// Explicitly create instance with current window to avoid issues when the
// module-level createDOMPurify() runs before DOM is fully ready
const DOMPurify = DOMPurifyFactory(window);

// DOMPurify config that allows mention chip SVG + citation marker attrs to survive sanitisation.
export const CHIP_DOMPURIFY_OPTIONS = {
  ADD_TAGS: ['svg', 'path', 'circle'],
  ADD_ATTR: [
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-dasharray',
    'fill-rule',
    'viewBox',
    'xmlns',
    'aria-hidden',
    'data-citation-end',
  ],
};

const renderMarkdownBody = (text: string): string => {
  const withNormalizedLinks = normalizeBareAmpLinks(text);
  const withChips = renderBodyWithChipLinks(withNormalizedLinks);
  const withTableDelimiters = ensureTableDelimiters(withChips);
  const html = String(marked.parse(withTableDelimiters));
  return wrapTablesForScroll(DOMPurify.sanitize(html, CHIP_DOMPURIFY_OPTIONS));
};

/**
 * Full markdown render pipeline for assistant message text parts.
 * Injects citation markers first (pre-markdown seam, preserving BE offsets),
 * then injects mention chip links, parses markdown, sanitises HTML, and wraps
 * tables for horizontal scroll.
 */
export const renderMessageHtml = (text: string, citations: CitationMarker[] = []): string => {
  const withCitations = injectCitationMarkers(text, citations);
  return renderMarkdownBody(withCitations);
};

// ---------------------------------------------------------------------------
// Streaming render pipeline
// ---------------------------------------------------------------------------

/**
 * Strips a trailing in-progress markdown link so it stays hidden as raw text
 * until it's complete, instead of flashing `[name](amp://audien…` or a closed
 * label with no parens yet (`[name]`) mid-stream.
 */
export const stripIncompleteLink = (text: string): string => text.replace(/\[[^\]]*\]?(?:\([^)]*)?$/, '');

const ampLabelFromLink = (link: string): string => {
  const firstSegment = link.slice('amp://'.length).split(/[/?]/)[0];
  return firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
};

/**
 * The agent occasionally emits a deep link as a bare bracketed URL
 * (`[amp://audiences]`) instead of a proper markdown link, which the mention
 * regex doesn't recognise. Normalizes it into `[Label](amp://audiences)` so
 * it still renders as a mention chip.
 *
 * It also handles the case where the agent uses the deep link as the link
 * *label* too (`[amp://audiences/343?…](amp://audiences/343?…)`): the label is
 * swapped for a nice label while the explicit URL is kept, so the URL doesn't
 * leak out as raw trailing text.
 */
export const normalizeBareAmpLinks = (text: string): string =>
  text.replace(
    /\[(amp:\/\/[^\]]+)\](\([^)]*\))?/g,
    (_, link: string, explicitUrl?: string) => `[${ampLabelFromLink(link)}]${explicitUrl ?? `(${link})`}`,
  );

const TABLE_DELIMITER_ROW_REGEX = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const PIPE_ROW_REGEX = /^\s*\|.*\|\s*$/;

const countTableColumns = (headerRow: string): number => {
  const cells = headerRow.split('|');
  const start = cells[0]?.trim() === '' ? 1 : 0;
  const end = cells[cells.length - 1]?.trim() === '' ? cells.length - 1 : cells.length;
  return Math.max(end - start, 1);
};

/**
 * GFM tables require a delimiter row (`|---|---|`) directly under the header
 * row to be recognised by `marked` — without it, the header renders as a raw
 * paragraph of pipes instead of a table. The agent occasionally emits a table
 * header with no delimiter row (or the delimiter never arrives). This inserts
 * a synthetic delimiter row after any header-like row that's missing one, so
 * the header always renders as a real table instead of raw markdown.
 */
export const ensureTableDelimiters = (text: string): string => {
  const lines = text.split('\n');

  // Track which lines fall inside fenced code blocks (``` … ```) so pipe rows
  // in CLI output / ASCII tables inside code fences are never mistaken for
  // markdown tables. The fence line itself is also excluded.
  let inCodeBlock = false;
  const isFencedLine = lines.map((line) => {
    if (line.trimStart().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      return true;
    }
    return inCodeBlock;
  });

  const result: string[] = [];

  lines.forEach((line, index) => {
    result.push(line);
    if (isFencedLine[index]) return;

    const isPipeRow = PIPE_ROW_REGEX.test(line);
    const prevIsPipeRow = index > 0 && !isFencedLine[index - 1] && PIPE_ROW_REGEX.test(lines[index - 1]);
    const nextLine = lines[index + 1];

    if (isPipeRow && !prevIsPipeRow && (nextLine === undefined || !TABLE_DELIMITER_ROW_REGEX.test(nextLine))) {
      result.push(`|${' --- |'.repeat(countTableColumns(line))}`);
    }
  });

  return result.join('\n');
};

/**
 * Strips a trailing incomplete GFM table block until it has a valid delimiter
 * row, and drops a trailing partial row while the table is still streaming.
 */
export const stripIncompleteTable = (text: string): string => {
  const endsWithNewline = text.endsWith('\n');
  const lines = text.split('\n');
  if (endsWithNewline) lines.pop();

  // Find the contiguous trailing run of table-row-like lines, bounded above by
  // a blank line or the start of the text. Lines must look like a pipe row
  // (leading + trailing '|') to avoid matching prose or shell pipelines that
  // merely contain a '|' character — except the very last line while still
  // streaming, which may not have its trailing '|' typed in yet.
  let runStart = lines.length;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    const isStreamingLastLine = i === lines.length - 1 && !endsWithNewline;
    const looksLikeTableRow = isStreamingLastLine ? /^\s*\|/.test(line) : PIPE_ROW_REGEX.test(line);
    if (line.trim() === '' || !looksLikeTableRow) break;
    runStart = i;
  }

  // No trailing table-ish run at all — nothing to strip.
  if (runStart === lines.length) return text;

  const runLines = lines.slice(runStart);
  const hasDelimiterRow = runLines.some((line) => TABLE_DELIMITER_ROW_REGEX.test(line));
  const precedingLines = lines.slice(0, runStart);

  // Table not yet recognisable (no delimiter row) — strip the whole run.
  if (!hasDelimiterRow) {
    return precedingLines.length > 0 ? `${precedingLines.join('\n')}\n` : '';
  }

  // Delimiter row found but the buffer is still streaming — the last line is
  // a partial row, drop it.
  if (!endsWithNewline) {
    const keptLines = lines.slice(0, -1);
    return keptLines.length > 0 ? `${keptLines.join('\n')}\n` : '';
  }

  // Complete table (or no trailing partial row) — leave unchanged.
  return text;
};

interface StreamingCitationContext {
  citations: CitationMarker[];
  partIndex: number;
  displayedLength: number;
}

// Close unclosed markdown constructs to prevent raw syntax from flashing during streaming
export const closeUnclosedMarkdown = (text: string): string => {
  let result = text;

  // Count ``` and close if odd
  const fenceMatches = result.match(/```/g);
  if (fenceMatches && fenceMatches.length % 2 !== 0) {
    result += '\n```';
  }

  // Count ** and close if odd
  const boldMatches = result.match(/\*\*/g);
  if (boldMatches && boldMatches.length % 2 !== 0) {
    result += '**';
  }

  // Count single ` (excluding ```) and close if odd
  const withoutFences = result.replace(/```/g, '');
  const backtickMatches = withoutFences.match(/`/g);
  if (backtickMatches && backtickMatches.length % 2 !== 0) {
    result += '`';
  }

  return result;
};

/**
 * Full markdown render pipeline for text that's still streaming in.
 * Injects citation markers (when provided), hides incomplete links/tables so
 * they don't flash as raw markdown, closes other unclosed constructs, then
 * renders mention chips + markdown the same way the final render does.
 */
export const renderStreamingHtml = (text: string, citationContext?: StreamingCitationContext): string => {
  const validCitations = citationContext?.citations.filter(
    (c) => (c.partIndex ?? 0) === citationContext.partIndex && c.end <= citationContext.displayedLength,
  );
  const withCitations =
    validCitations && validCitations.length > 0 ? injectCitationMarkers(text, validCitations) : text;
  const withoutIncompleteLink = stripIncompleteLink(withCitations);
  const withoutIncompleteTable = stripIncompleteTable(withoutIncompleteLink);
  const closed = closeUnclosedMarkdown(withoutIncompleteTable);
  return renderMarkdownBody(closed);
};
